import Button from '@/component/Button'
import Loading from '@/component/Loading'
import Toast from '@/component/Toast'
import TGASDK from '@/config/TGASDK'
import AuthContext from '@/context/AuthContext'
import WalletContext from '@/context/WalletContext'
import useInviteRewardConfig from '@/hook/useInviteRewardConfig'
import useRuntime from '@/hook/useRuntime'
import { PopUpStore } from '@/store/PopupStore'
import { Invite } from '@kulee/tga-sdk'
import type { InviteSummary } from '@kulee/tga-sdk/types/types/invite'
import { useMutation } from '@tanstack/react-query'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCopyToClipboard } from 'react-use'

const InviteMain: React.FC<PropType> = () => {
  const { t } = useTranslation(['wallet', 'invite'])
  const { user } = useContext(AuthContext)
  const { init: initWallet } = useContext(WalletContext)
  const { channel } = useRuntime()
  const [state, copyToClipboard] = useCopyToClipboard()
  const { data } = useInviteRewardConfig()
  const [inviteSummary, setInviteSummary] = useState<InviteSummary>()
  const url = `${window.location.origin}/${channel}/?inviteCode=${user?.inviteCode}`

  const mutation = useMutation({
    mutationFn: () => {
      return TGASDK.module.Invite.service.claimAllReward()
    }
  })

  const init = async () => {
    const { data } = await TGASDK.module.Invite.service.getInviteSummary()
    setInviteSummary(data)
  }

  const handleCopy = () => {
    if (!user?.inviteCode) {
      return
    }
    copyToClipboard(user.inviteCode)
  }

  const handleInvite = () => {
    // 开始分享
    PopUpStore.setSharePopup({
      visible: true,
      params: {
        url,
        text: t('tip.shareText', { ns: 'invite' })
      }
    })
  }

  const handleClaim = async () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        init()
        // 刷新钱包
        initWallet()

        Toast.show({
          content: t('message.claimSuccess', { ns: 'invite' }),
          icon: 'success'
        })
      }
    })
  }

  useEffect(() => {
    const { error, value } = state

    if (error) {
      Toast.show({
        content: t('message.claimSuccess', { ns: 'invite' })
      })
    } else if (value) {
      Toast.show({
        content: t('message.copySuccess', { ns: 'invite' })
      })
    }
  }, [state])

  useEffect(() => {
    TGASDK.addModule(Invite)
    init()
  }, [])

  if (!inviteSummary || !data) {
    return <Loading className="h-40" />
  }

  return (
    <div className="px-md flex flex-col gap-md">
      <div className="flex justify-center items-center gap-md">
        <div className="text-xl font-bold flex-grow">
          <p>{t('tip.rewardSlogan', { ns: 'invite' })}</p>
          <p>
            {!!data?.bestRebateRate && (
              <span className="text-semantic-error">
                {t('tip.rewardSloganRebateRate', { ns: 'invite', bestRebateRate: data.bestRebateRate })}
              </span>
            )}
            {!!data?.bestDiamondRewardAmount && (
              <>
                <span className="mx-sm">+</span>
                <span className="text-semantic-error">
                  {t('tip.rewardSloganDiamondRewardAmount', {
                    ns: 'invite',
                    bestDiamondRewardAmount: data.bestDiamondRewardAmount
                  })}
                </span>
              </>
            )}
            {!!data?.bestGoldRewardAmount && (
              <>
                <span className="mx-sm">+</span>
                <span className="text-semantic-error">
                  {t('tip.rewardSloganGoldRewardAmount', {
                    ns: 'invite',
                    bestGoldRewardAmount: data.bestGoldRewardAmount
                  })}
                </span>
              </>
            )}
          </p>
        </div>
        <img src={require('@/asset/image/invite_rewards_1.png')} alt="invite" className="w-24" />
      </div>
      <div className="flex flex-col gap-md">
        <div className="border-primary border rounded-md flex items-center px-4 py-3 bg-white">
          <div className="flex-grow">
            <p className="text-xs text-black/65">{t('tip.inviteCode', { ns: 'invite' })}</p>
            <span className="font-semibold text-base">{user?.inviteCode}</span>
          </div>
          <Button type="secondary" size="small" onClick={handleCopy}>
            {t('btn.copy', { ns: 'invite' })}
          </Button>
        </div>
        <div>
          <Button type="primary" size="middle" className="w-full" onClick={handleInvite}>
            {t('btn.invite', { ns: 'invite' })}
          </Button>
        </div>
      </div>
      <div>
        <h3 className="!text-lg !font-semibold mb-sm">{t('title.reward', { ns: 'invite' })}</h3>
        <div>
          <div className="grid grid-cols-2 gap-sm">
            {!!data?.bestRebateRate && (
              <div className="bg-primary/25 px-md py-sm rounded-md text-center">
                <div>{t('tip.rebateRewardAmount', { ns: 'invite' })}</div>
                <div className="text-lg font-semibold">{inviteSummary?.claimedRechargeTokenReward || 0}</div>
              </div>
            )}

            {!!data?.bestDiamondRewardAmount && (
              <div className="bg-primary/25 px-md py-sm rounded-md text-center">
                <div>{t('tip.diamondRewardAmount', { ns: 'invite' })}</div>
                <div className="text-lg font-semibold">{inviteSummary?.claimedInviteTokenReward || 0}</div>
              </div>
            )}

            {!!data?.bestGoldRewardAmount && (
              <div className="bg-primary/25 px-md py-sm rounded-md text-center">
                <div>{t('tip.goldRewardAmount', { ns: 'invite' })}</div>
                <div className="text-lg font-semibold">{inviteSummary?.claimedInviteCoinReward || 0}</div>
              </div>
            )}

            <div className="bg-primary/25 px-md py-sm rounded-md text-center">
              <div>{t('tip.userAmount', { ns: 'invite' })}</div>
              <div className="text-lg font-semibold">{inviteSummary?.inviteNum || 0}</div>
            </div>
          </div>
          <div className="mt-sm text-center">
            <Button
              type="primary"
              size="small"
              disabled={
                inviteSummary?.unclaimedInviteTokenReward +
                  inviteSummary?.unclaimedRechargeTokenReward +
                  inviteSummary?.unclaimedInviteCoinReward ===
                0
              }
              loading={mutation.isPending}
              onClick={handleClaim}
            >
              {(!!data?.bestRebateRate || !!data?.bestDiamondRewardAmount) &&
                inviteSummary?.unclaimedInviteTokenReward +
                  inviteSummary?.unclaimedRechargeTokenReward +
                  t('unit.diamond', {
                    ns: 'wallet',
                    count: inviteSummary?.unclaimedInviteTokenReward + inviteSummary?.unclaimedRechargeTokenReward
                  })}{' '}
              {!!data?.bestGoldRewardAmount &&
                inviteSummary?.unclaimedInviteCoinReward +
                  t('unit.gold', {
                    ns: 'wallet',
                    count: inviteSummary?.unclaimedInviteCoinReward
                  })}{' '}
              {t('btn.claim', { ns: 'invite' })}
            </Button>
          </div>
        </div>
      </div>
      <div>
        <h3 className="!text-lg !font-semibold mb-sm">{t('title.rule', { ns: 'invite' })}</h3>
        <div>
          {data?.rules.map((item, index) => (
            <div className="flex gap-sm" key={item.key}>
              <div className="relative flex flex-col">
                <div className="bg-black/25 rounded-full w-6 h-6 text-white flex items-center justify-center font-semibold">
                  {index + 1}
                </div>
                {index < data.rules.length - 1 && (
                  <div className="absolute top-6 left-[12px] z-[1] border-l border-neutral-300 h-[calc(100%-24px)]"></div>
                )}
              </div>
              <div className="text-base mb-md">{t(`rules.${item.key}`, { ns: 'invite', ...item.data })}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

interface PropType {}

export default InviteMain
